"use client";

import { useMemo } from "react";
import {
  QUERY_STATUS,
  deriveResult,
  syncLookupResult,
  type QueryOptions,
  type QueryResult,
} from "@/types/async-protocol";
import { useApi } from "@/contexts/api";
import { useQueryHook } from "@/lib/query/use-query-hook";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import { isRoundFocusedAt } from "@cabal/shared/domain/session/product/focus.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type {
  Session,
  SessionOverviewResponse,
} from "@cabal/shared/domain/session/types.js";
import type { VersionSummary } from "@cabal/shared/domain/session/version/types.js";
import { useSessionDataContext } from "./context";
import { selectSessionDataQueryOptions } from "./hook-selectors";
import { sessionDataKeys } from "./keys";

export function useSession(): QueryResult<SessionOverviewResponse> & {
  session: SessionOverviewResponse | null;
  loading: boolean;
  notFound: boolean;
  teamDefaultStartingRounds: number | null;
} {
  const context = useSessionDataContext();
  const loading = context.loading.session;

  return {
    data: context.session,
    session: context.session,
    status: loading ? QUERY_STATUS.Loading : QUERY_STATUS.Success,
    error: null,
    refetch: () => {
      void context.actions.refreshSession();
    },
    loading,
    notFound: context.notFound,
    teamDefaultStartingRounds: context.teamDefaultStartingRounds,
  };
}

export function useThinkingScore(): {
  value: number | null;
  isLoading: boolean;
} {
  const { session, loading } = useSession();
  return { value: session?.thinkingScore ?? null, isLoading: loading };
}

export function useSessionSettings(
  options?: QueryOptions,
): QueryResult<Session> & {
  sessionSettings: Session | null;
  isLoading: boolean;
} {
  const api = useApi();
  const context = useSessionDataContext();
  const query = useQueryHook<Session>({
    queryKey: sessionDataKeys.settings(context.sessionId),
    queryFn: () => api.sessions.byId(context.sessionId).get(),
    options: selectSessionDataQueryOptions({ isReady: true, options }),
  });

  return {
    ...query,
    sessionSettings: query.data,
    isLoading: query.status === QUERY_STATUS.Loading,
  };
}

export function useSessionMembers(): QueryResult<Member[]> & {
  members: Member[];
  membersMap: Record<string, Member>;
  loading: boolean;
} {
  const context = useSessionDataContext();
  const contextMembers = context.members;
  const members = useMemo(
    () => Object.values(contextMembers),
    [contextMembers],
  );
  const loading = context.loading.members;

  return {
    data: members,
    members,
    membersMap: context.members,
    status: loading ? QUERY_STATUS.Loading : QUERY_STATUS.Success,
    error: null,
    refetch: () => {
      void context.actions.refreshMembers();
    },
    loading,
  };
}

export function useMembers(): ReturnType<typeof useSessionMembers> {
  return useSessionMembers();
}

export function useSessionMember(
  memberId: string | null | undefined,
): QueryResult<Member> & {
  member: Member | null;
} {
  const context = useSessionDataContext();
  const member = memberId ? (context.members[memberId] ?? null) : null;

  return {
    data: member,
    member,
    status: context.loading.members
      ? QUERY_STATUS.Loading
      : QUERY_STATUS.Success,
    error: null,
    refetch: () => {
      void context.actions.refreshMembers();
    },
  };
}

export function useMember(
  memberId: string | null | undefined,
): QueryResult<Member> {
  const { data, status, error, refetch } = useSessionMember(memberId);
  return { data, status, error, refetch };
}

export function useSessionProducts(): QueryResult<
  Record<string, ProductSummary>
> & {
  products: Record<string, ProductSummary>;
  loading: boolean;
} {
  const context = useSessionDataContext();
  const loading = context.loading.products;

  return {
    data: context.products,
    products: context.products,
    status: loading ? QUERY_STATUS.Loading : QUERY_STATUS.Success,
    error: null,
    refetch: () => {
      void context.actions.refreshProducts();
    },
    loading,
  };
}

/**
 * Member ids that own at least one round-focus product this round. During
 * Reflect/Write these are the only operatives actually working, so only they
 * render as active (the rest sit idle). Derived from live product state:
 * `owner.actorId` is the owning member's id.
 */
export function useRoundFocusWriterMemberIds(): ReadonlySet<string> {
  const { products } = useSessionProducts();
  const { session } = useSession();
  return useMemo(
    () =>
      new Set(
        Object.values(products).flatMap((product) =>
          session && isRoundFocusedAt({
            roundFocusUntilStepstamp: product.roundFocusUntilStepstamp,
            atStepstamp: session.currentStepstamp,
          }) && product.owner
            ? [product.owner.actorId]
            : [],
        ),
      ),
    [products, session],
  );
}

export function useProduct(
  productId: string | null | undefined,
): QueryResult<ProductSummary> {
  const productsQuery = useSessionProducts();
  return deriveResult(productsQuery, (products) =>
    productId ? (products[productId] ?? null) : null,
  );
}

export function useVersion(
  versionId: string | null | undefined,
): QueryResult<VersionSummary> {
  const context = useSessionDataContext();
  const version = versionId ? (context.versions[versionId] ?? null) : null;
  return syncLookupResult(version);
}

export function useCollab(
  collabId: string | null | undefined,
): QueryResult<Collab> {
  const context = useSessionDataContext();
  const collab = collabId
    ? (context.collabs[collabId] ?? context.chatMessages[collabId] ?? null)
    : null;
  return syncLookupResult(collab);
}

export function useProductChildren(
  parentId: string | null | undefined,
): QueryResult<ProductSummary[]> {
  const productsQuery = useSessionProducts();
  return useMemo(
    () =>
      deriveResult(productsQuery, (products) => {
        const parent = parentId ? (products[parentId] ?? null) : null;
        if (!parent) return null;
        return parent.subproductIds
          .map((id) => products[id])
          .filter((product): product is ProductSummary => Boolean(product));
      }),
    [productsQuery, parentId],
  );
}
